import { useState, useMemo, useCallback } from 'react';
import { toast } from 'react-hot-toast';

export const useLivestreamForm = () => {
  // Form state
  const [livestreamName, setLivestreamName] = useState('');
  const [date, setDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  const [selectedCourtId, setSelectedCourtId] = useState('');
  const [selectedCamera, setSelectedCamera] = useState('');
  const [selectedCameraId, setSelectedCameraId] = useState('');
  const [gameType, setGameType] = useState('');
  const [discountCode, setDiscountCode] = useState('');
  const [teamAName, setTeamAName] = useState('');
  const [teamBName, setTeamBName] = useState('');
  const [sponsorName, setSponsorName] = useState('');
  const [sponsorLogo, setSponsorLogo] = useState('');
  const [logoFile, setLogoFile] = useState(null);
  const [sponsors, setSponsors] = useState([]);
  const [tempSponsorName, setTempSponsorName] = useState('');
  const [viewersPrice, setViewersPrice] = useState(0);

  // Time validation function to handle midnight crossing
  const validateTimeRange = useCallback(() => {
    if (!date || !startTime || !endTime) return { valid: true, errors: [] };
    
    // Create Date objects with proper timezone handling
    const startDateTime = new Date(`${date}T${startTime}:00`);
    const endDateTime = new Date(`${date}T${endTime}:00`);
    
    // Handle midnight crossing - if end time is earlier than start time in 24-hour format
    // This means the event crosses midnight (e.g., 23:00 to 01:00)
    if (endDateTime < startDateTime) {
      endDateTime.setDate(endDateTime.getDate() + 1);
    }
    
    // Check if start and end times are the same
    if (startDateTime.getTime() === endDateTime.getTime()) {
      return { valid: false, errors: ['Start time and end time cannot be the same'] };
    }
    
    // Check if duration is reasonable (e.g., not longer than 24 hours)
    const duration = endDateTime - startDateTime;
    const maxDuration = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
    
    if (duration > maxDuration) {
      return { valid: false, errors: ['Livestream duration cannot exceed 24 hours'] };
    }

    return { valid: true, errors: [] };
  }, [date, startTime, endTime]);

  // Helper function to calculate livestream duration
  const calculateDuration = () => {
    if (!date || !startTime || !endTime) return null;
    
    // Create Date objects with proper timezone handling
    const startDateTime = new Date(`${date}T${startTime}:00`);
    const endDateTime = new Date(`${date}T${endTime}:00`);
    
    // Handle midnight crossing - if end time is earlier than start time in 24-hour format
    // This means the event crosses midnight (e.g., 23:00 to 01:00)
    if (endDateTime < startDateTime) {
      endDateTime.setDate(endDateTime.getDate() + 1);
    }
    
    const duration = endDateTime - startDateTime;
    const hours = Math.floor(duration / (1000 * 60 * 60));
    const minutes = Math.floor((duration % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  // Form validation
  const isFormValid = useMemo(() => {
    const requiredFields = {
      'Livestream Name': livestreamName,
      'Court': selectedCourtId,
      'Camera': selectedCameraId,
      'Date': date,
      'Start Time': startTime,
      'End Time': endTime
    };

    const missingFields = Object.entries(requiredFields)
      .filter(([_, value]) => !value)
      .map(([field]) => field);

    if (missingFields.length > 0) {
      return { valid: false, errors: missingFields };
    }

    const sponsorsWithoutLogos = sponsors.filter(s => !s.logoUrl);
    if (sponsorsWithoutLogos.length > 0) {
      return { valid: false, errors: ['Please upload logos for all sponsors before scheduling'] };
    }

    // Add time validation for midnight crossing and duration
    const timeValidation = validateTimeRange();
    if (!timeValidation.valid) {
      return { valid: false, errors: [...missingFields, ...timeValidation.errors] };
    }

    return { valid: true, errors: [] };
  }, [livestreamName, selectedCourtId, selectedCameraId, date, startTime, endTime, sponsors, validateTimeRange]);

  // Sponsor management
  const handleAddSponsor = () => {
    if (!tempSponsorName.trim()) {
      toast.error('Please enter a sponsor name');
      return;
    }

    const newSponsor = {
      sponsorName: tempSponsorName.trim(),
      logoUrl: '' // Will be updated when logo is uploaded
    };

    setSponsors(prev => [...prev, newSponsor]);
    setTempSponsorName('');
  };

  const handleRemoveSponsor = (index) => {
    setSponsors(prev => prev.filter((_, i) => i !== index));
  };

  const handleLogoUpload = (index, objectKey) => {
    setSponsors(prev => {
      const updated = prev.map((sponsor, i) => 
        i === index ? { ...sponsor, logoUrl: objectKey } : sponsor
      );
      return updated;
    });
  };

  // Camera management
  const handleCameraChange = (e, availableCameras) => {
    const cameraId = e.target.value;
    const camera = availableCameras.find(cam => cam.id === Number(cameraId));
    if (camera) {
      setSelectedCamera(camera.name); // Set the actual camera name
      setSelectedCameraId(camera.id);
    } else {
      setSelectedCamera('');
      setSelectedCameraId('');
    }
  };

  // Form submission data preparation
  const prepareLivestreamData = (courts) => {
    const selectedCourtData = courts.find(c => c.id === Number(selectedCourtId));
    
    let startIso = `${date}T${startTime}:00`;
    let endIso = `${date}T${endTime}:00`;
    
    // Handle midnight crossing - if end time is earlier than start time in 24-hour format
    // This means the event crosses midnight (e.g., 23:00 to 01:00)
    const startDateTime = new Date(startIso);
    const endDateTime = new Date(endIso);
    
    if (endDateTime < startDateTime) {
      // End time is on the next day
      const nextDay = new Date(date);
      nextDay.setDate(nextDay.getDate() + 1);
      const nextDayString = nextDay.toISOString().split('T')[0];
      endIso = `${nextDayString}T${endTime}:00`;
    }

    // Ensure we have a valid description
    const description = livestreamName?.trim() || 'Livestream Event';

    const data = {
      // Required fields - only send fields that exist in backend DTO
      description: description,
      startTime: startIso,
      endTime: endIso,
      
      // Court and camera
      courtId: selectedCourtId ? Number(selectedCourtId) : undefined,
      cameraId: selectedCameraId ? Number(selectedCameraId) : undefined,
      cameraName: selectedCamera || undefined,
      
      // Pricing
      price: viewersPrice || 0,
      viewersPrice: viewersPrice || 0,
      hourlyPrice: 0,
      usageFee: 0,
      requiresPayment: !isPublic,
      freeLivestream: viewersPrice === 0,
      
      // Game details
      gameType: gameType || undefined,
      teamAName: teamAName || undefined,
      teamBName: teamBName || undefined,
      teamAScore: 0,
      teamBScore: 0,
      doubles: false,
      
      // Sponsors and branding
      sponsors: sponsors.length > 0 ? sponsors : undefined,
      logoType: "AKTURE",
      
      // Status
      status: "SCHEDULED",
      
      // Recording
      recordingEnabled: false,
      recordingUrl: undefined,
      
      // Additional fields
      ownerPaid: false,
      liveStreamPurchaseIds: [],
      viewerIds: []
    };

    console.log('Prepared livestream data:', data);
    return data;
  };

  // Reset form
  const resetForm = () => {
    setLivestreamName('');
    setDate('');
    setStartTime('');
    setEndTime('');
    setIsPublic(true);
    setSelectedCourtId('');
    setSelectedCamera('');
    setSelectedCameraId('');
    setGameType('');
    setDiscountCode('');
    setTeamAName('');
    setTeamBName('');
    setSponsorName('');
    setSponsorLogo('');
    setLogoFile(null);
    setSponsors([]);
    setTempSponsorName('');
    setViewersPrice(0);
  };

  // Form state object
  const formState = {
    livestreamName,
    date,
    startTime,
    endTime,
    isPublic,
    selectedCourtId,
    selectedCamera,
    selectedCameraId,
    gameType,
    discountCode,
    teamAName,
    teamBName,
    sponsorName,
    sponsorLogo,
    logoFile,
    sponsors,
    tempSponsorName,
    viewersPrice
  };

  // Setters object
  const setters = {
    setLivestreamName,
    setDate,
    setStartTime,
    setEndTime,
    setIsPublic,
    setSelectedCourtId,
    setSelectedCamera,
    setSelectedCameraId,
    setGameType,
    setDiscountCode,
    setTeamAName,
    setTeamBName,
    setSponsorName,
    setSponsorLogo,
    setLogoFile,
    setSponsors,
    setTempSponsorName,
    setViewersPrice
  };

  return {
    formState,
    setters,
    isFormValid,
    handleAddSponsor,
    handleRemoveSponsor,
    handleLogoUpload,
    handleCameraChange,
    prepareLivestreamData,
    calculateDuration,
    resetForm
  };
}; 